import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Project, ProjectStatus, normalizeStatus } from '../types/project';
import { User, UserRole } from '../types/user';
import { getCurrentUser } from '../services/userService';
import { counties, subCounties, facilities } from '../data/locations';
import { CHS_DOMAINS, CHS_SUBDOMAINS } from '../types/project-form';
import UpdateProgress from '../components/UpdateProgress';
import { TestCycle } from '../types/test-cycle';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { projectService } from '../services/api';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TopBar from '../components/TopBar';
import {
  isProjectDueForUpdate,
  getProjectDueDate,
  getDaysUntilDue,
  getProjectDueStatus,
  getCQIProjectStatus,
  getCQIStatusSummary,
  getGroupedCQIStatusSummary,
  isOnCourseStatus,
  CQIProjectStatus
} from '../utils/projectHelpers';
import { canUpdateProgress } from '../utils/permissions';

interface ProjectListProps {
  status?: 'ongoing' | 'nearCompletion' | 'completed' | 'archived' | 'draft';
  onLogout: () => void;
}

const ProjectList = ({ status = 'ongoing', onLogout }: ProjectListProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
    open: boolean;
  }>({ message: '', severity: 'info', open: false });
  
  // Handle closing the notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Mock projects data for development/fallback
  const mockProjects: Project[] = [
    {
      id: '1',
      programmeArea: 'HIV Prevention',
      subcategory: 'Testing',
      department: 'Main Lab',
      projectCategory: 'Patient Safety',
      projectTitle: 'Improved HIV Testing Protocol',
      responsiblePerson: 'John Doe',
      participants: [
        { fullName: 'Jane Smith', email: 'jane@example.com', phone: '123-456-7890', role: 'Nurse' }
      ],
      problemStatement: 'Current testing process has delays causing patient dissatisfaction',
      goalStatement: 'Reduce testing turnaround time by 50%',
      indicatorDescription: 'Testing turnaround time',
      useCustomIndicator: true,
      indicatorNumerator: 'Number of tests completed within target time',
      indicatorDenominator: 'Total number of tests conducted',
      improvementFrom: 40,
      improvementTo: 90,
      problemIdentificationTool: 'paretoChart',
      rootCauseAnalysisTool: 'fishbone',
      measurementFrequency: 'WEEKLY',
      firstCycleDate: '2025-06-01',
      projectEndDate: '2025-08-01',
      initiativeSource: 'CIHEB CONNECT',
      status: 'ongoing',
      createdAt: '2025-06-01',
      updatedAt: '2025-06-15',
      testCycles: [],
      // Add missing required fields
      feedback: ['Good progress on turnaround time reduction'],
      lastModified: '2025-06-15T10:30:00Z',
      createdBy: 'admin@cqi.org',
      comments: ['Initial setup complete', 'First team meeting scheduled'],
      resources: ['SOP Document', 'Training Materials'],
      facility: 'Central Hospital',
      description: 'Project aimed at improving HIV testing protocols and reducing turnaround time',
      startDate: '2025-06-01',
      endDate: '2025-08-01'
    },
    {
      id: '2',
      programmeArea: 'MNCH',
      subcategory: 'Maternity',
      department: 'Maternity',
      projectCategory: 'Clinical Effectiveness',
      projectTitle: 'Reducing Maternal Waiting Times',
      responsiblePerson: 'Mary Johnson',
      participants: [
        { fullName: 'Robert Williams', email: 'robert@example.com', phone: '123-456-7890', role: 'Doctor' }
      ],
      problemStatement: 'Long waiting times for antenatal services',
      goalStatement: 'Reduce waiting time by 60%',
      indicatorDescription: 'Antenatal waiting time',
      useCustomIndicator: false,
      indicatorNumerator: '',
      indicatorDenominator: '',
      improvementFrom: 30,
      improvementTo: 85,
      problemIdentificationTool: 'decisionMatrix',
      rootCauseAnalysisTool: 'fiveWhy',
      measurementFrequency: 'WEEKLY',
      firstCycleDate: '2025-06-10',
      projectEndDate: '2025-09-10',
      initiativeSource: 'COUNTY GOVT OF SIAYA',
      status: 'ongoing',
      createdAt: '2025-06-10',
      updatedAt: '2025-06-20',
      testCycles: [],
      // Add missing required fields
      feedback: ['Need to improve booking system', 'Consider additional staff during peak hours'],
      lastModified: '2025-06-20T14:45:00Z',
      createdBy: 'manager@cqi.org',
      comments: ['Process mapping completed', 'Staff briefing scheduled for next week'],
      resources: ['Appointment System Guide', 'Process Flow Chart'],
      facility: 'Siaya County Hospital',
      description: 'Project focused on streamlining maternal care workflow to reduce waiting times',
      startDate: '2025-06-10',
      endDate: '2025-09-10'
    }
  ];

  // Check for success message when component mounts
  useEffect(() => {
    if (location.state?.message) {
      // Display notification with the message from location state
      setNotification({
        message: location.state.message,
        severity: location.state.error ? 'error' : 'success',
        open: true
      });
      
      // Clear the message from location state to prevent showing it again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        
        // First check if projects were passed from Dashboard (already filtered by jurisdiction and status)
        if (location.state?.projects && Array.isArray(location.state.projects)) {
          
          // Use the projects that were passed in navigation state
          setProjects(location.state.projects);
          setIsLoading(false);
          return; // Exit early - no need to fetch all projects again
        }
        
        
        // Check if we need to refresh projects (from a new submission)
        const refreshNeeded = location.state?.refreshProjects === true;
        
        // Get current user for jurisdiction filtering
        const user = getCurrentUser();
        
        // Set current user in state for UI filtering
        setCurrentUser(user);
        
        // Initialize filters based on user role
        if (user) {
          let roleDefaults: Partial<Filters> = {};

          // For COUNTY_USER, pre-select their county
          if (user.role === UserRole.COUNTY_USER && user.county) {
            roleDefaults = { county: user.county };
          }

          // For SUBCOUNTY_USER, pre-select their subcounty and county
          if (user.role === UserRole.SUBCOUNTY_USER && user.subCounty) {
            // Find the county for this subcounty
            const subcountyData = subCounties.find(sc => sc.name === user.subCounty);
            const countyName = subcountyData?.countyName || 'all';

            roleDefaults = { county: countyName, subcounty: user.subCounty };
          }

          // For FACILITY_USER, pre-select their facility, subcounty, and county
          if (user.role === UserRole.FACILITY_USER && user.facility) {
            // Find the subcounty for this facility
            const facilityData = facilities.find(f => f.name === user.facility);
            const subcountyName = facilityData?.subCountyName || 'all';

            // Find the county for this subcounty
            const subcountyData = subCounties.find(sc => sc.name === subcountyName);
            const countyName = subcountyData?.countyName || 'all';

            roleDefaults = {
              county: countyName,
              subcounty: subcountyName,
              facility: user.facility
            };
          }

          setFilters(prev => ({ ...prev, ...roleDefaults }));
          setAppliedFilters(prev => ({ ...prev, ...roleDefaults }));
        }
        
        
        try {
          // Fetch all projects from the API
          const allProjects = await projectService.getAllProjects();
          
          
          // Filter projects based on user jurisdiction
          let jurisdictionProjects = allProjects;
          
          if (user) {
            // Apply role-based filtering:
            // FACILITY_USER: only sees projects from their facility
            // SUBCOUNTY_USER: sees all projects from facilities in their subcounty
            // COUNTY_USER: sees all projects from subcounties in their county
            // NATIONAL_USER or CQI_MANAGER: sees all projects
            
            if (user.role === UserRole.FACILITY_USER && user.facility) {
              jurisdictionProjects = allProjects.filter(project => 
                project.facility === user.facility
              );
            } 
            else if (user.role === UserRole.SUBCOUNTY_USER && user.subCounty) {
              jurisdictionProjects = allProjects.filter(project => 
                project.subcounty === user.subCounty
              );
            }
            else if (user.role === UserRole.COUNTY_USER && user.county) {
              jurisdictionProjects = allProjects.filter(project => 
                project.county === user.county
              );
    
            }
            // CQI_MANAGER and NATIONAL_USER see all projects
          }
          
          // Check for fishbone issues
          const fishboneProjects = jurisdictionProjects.filter(p => 
            p.rootCauseAnalysisTool === 'fishbone' || 
            p.fishBoneAnalysis || 
            p.peopleCauses || 
            p.processCauses || 
            p.policyCauses || 
            p.placeCauses || 
            p.provisionCauses
          );
      
          
          // Sort projects by creation date (most recent first)
          const sortedProjects = jurisdictionProjects.sort((a: Project, b: Project) => {
            return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
          });
          
          
          
          
          // We're now using imported location data instead of extracting from projects
          // No need to extract unique values anymore
          
          setProjects(sortedProjects);
          setError(null);
          
          if (sortedProjects.length === 0) {
            setError('No projects found');
          }
        } catch (apiError) {
          console.error('API fetch failed:', apiError);
          
          // If API fails, try to get projects from localStorage as backup
          const localProjects = JSON.parse(localStorage.getItem('projects') || '[]');
          
          if (localProjects && Array.isArray(localProjects) && localProjects.length > 0) {
            // Filter out draft projects from local storage
            const submittedLocalProjects = localProjects.filter((project: Project) => 
              project.status && project.status.toLowerCase() !== 'draft'
            );
            
            setProjects(submittedLocalProjects);
            setError('Using locally stored projects - API unavailable');
          } else {
            // Last resort: use empty array since we want to show only submitted projects
            setProjects([]);
            setError('No submitted projects found - API unavailable');
          }
        }
      } catch (err) {
        console.error('Error in fetchProjects:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch projects');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [location.state]);

  // Get current user for role-based access control
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isAddTestCycleOpen, setIsAddTestCycleOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const handleProjectClick = (project: Project, event?: React.MouseEvent) => {
    // Prevent navigation if clicking on buttons inside the card
    if (event && event.defaultPrevented) {
      return;
    }
    
    // Use MongoDB _id if available, otherwise fall back to id
    const projectId = project._id || project.id;
    if (!projectId) {
      console.error('Cannot navigate: Project missing ID:', project);
      return;
    }
    
    // Draft projects should open in the AddProject form for editing
    if (project.status === 'draft') {
      navigate(`/add-project?draft=${projectId}`);
      return;
    }
    
    navigate(`/project/${projectId}`, { state: { selectedProject: project } });
  };
  
  const handleAddTestCycleClick = (event: React.MouseEvent, project: Project) => {
    event.stopPropagation(); // Prevent the card click from triggering
    
    // Ensure the project has a valid ID before proceeding
    if (!project._id && !project.id) {
      console.error('Project is missing both _id and id fields:', project);
      return;
    }
    
    setSelectedProject(project);
    setIsAddTestCycleOpen(true);
  };

  const handleTestCycleSubmit = async (testCycleData: any) => {
    if (!selectedProject) return;
    
    // Use _id if available, otherwise fall back to id
    const projectId = selectedProject._id || selectedProject.id;
    
    // Make sure we have a valid project ID
    if (!projectId) {
      console.error('No valid ID found for project:', selectedProject);
      return;
    }
    
    try {
      const updatedProject = await projectService.addTestCycle(projectId, {
        id: `tc-${Date.now()}`, // Generate a temporary ID
        projectId: projectId,
        ...testCycleData
      });
      
      // Update the project in our local state
      setProjects(projects.map(p => {
        // Match by either _id or id
        if ((p._id && p._id === projectId) || (p.id && p.id === projectId)) {
          return updatedProject;
        }
        return p;
      }));
      
      // Close the dialog
      setIsAddTestCycleOpen(false);
    } catch (error) {
      console.error('Failed to add test cycle:', error);
      setError('Failed to add test cycle. Please try again.');
    }
  };

  const handleAddTestCycle = (testCycle: TestCycle) => {
    
    // For now, we'll just close the dialog
    setIsAddTestCycleOpen(false);
    setSelectedProject(null);
  };
  
  type FilterDomain = 'all' | string;
  type FilterSubDomain = 'all' | string;
  type FilterCQIStatus = 'all' | CQIProjectStatus | 'on_course';
  type FilterOnCourseSubset = 'all' | 'up_to_date' | 'due_for_update' | 'due_today';

  interface Filters {
    search: string;
    cqiStatus: FilterCQIStatus;
    onCourseSubset: FilterOnCourseSubset;
    domain: FilterDomain;
    subDomain: FilterSubDomain;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    county: string;
    subcounty: string;
    facility: string;
  }

  const emptyFilters: Filters = {
    search: '',
    cqiStatus: 'all',
    onCourseSubset: 'all',
    domain: 'all',
    subDomain: 'all',
    fromDate: null,
    toDate: null,
    county: 'all',
    subcounty: 'all',
    facility: 'all',
  };

  // Draft filters are what the user is editing in the panel;
  // appliedFilters are the criteria actually used to filter the list.
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(emptyFilters);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters({ ...emptyFilters });
    setAppliedFilters({ ...emptyFilters });
  };

  const handleDateChange = (field: 'fromDate' | 'toDate', value: Dayjs | null) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredProjects = projects.filter((project: Project) => {
    
    
    // Derive county and subcounty from facility if they're not set
    const projectFacility = project.facility;
    let projectSubcounty = project.subcounty;
    let projectCounty = project.county;
    
    // If subcounty or county is missing but facility is present, try to derive them
    if (projectFacility && (!projectSubcounty || !projectCounty)) {
      
      // Find facility in our known facilities list
      const facilityData = facilities.find(f => f.name === projectFacility);
      
      if (facilityData) {
        // Set subcounty from facility data if missing
        if (!projectSubcounty) {
          projectSubcounty = facilityData.subCountyName;
        }
        
        // Find county from subcounty if missing
        if (!projectCounty && projectSubcounty) {
          const subcountyData = subCounties.find(sc => sc.name === projectSubcounty);
          
          if (subcountyData) {
            projectCounty = subcountyData.countyName;
          } 
        }
      } 
    }
    
    
    const matchesSearch = project.projectTitle.toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
      project.problemStatement.toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
      project.responsiblePerson.toLowerCase().includes(appliedFilters.search.toLowerCase());

    // CQI Status filtering - only applies to ongoing projects
    let matchesCQIStatus = true;
    if (appliedFilters.cqiStatus !== 'all') {
      if (project.status === 'ongoing') {
        const projectCQIStatus = getCQIProjectStatus(project);

        if (appliedFilters.cqiStatus === 'on_course') {
          // If "On Course" is selected, check if project is in any on-course status
          const isProjectOnCourse = isOnCourseStatus(projectCQIStatus.status);

          // If on-course subset filter is also applied
          if (appliedFilters.onCourseSubset !== 'all' && isProjectOnCourse) {
            matchesCQIStatus = projectCQIStatus.status === appliedFilters.onCourseSubset;
          } else {
            matchesCQIStatus = isProjectOnCourse;
          }
        } else {
          // For specific status filtering
          matchesCQIStatus = projectCQIStatus.status === appliedFilters.cqiStatus;
        }
      } else {
        // For non-ongoing projects, exclude them when filtering by CQI status
        matchesCQIStatus = false;
      }
    }

    const matchesDomain = appliedFilters.domain === 'all' || project.domain === appliedFilters.domain;

    const matchesSubDomain = appliedFilters.subDomain === 'all' || project.subDomain === appliedFilters.subDomain;
    
    // First apply role-based filtering (only show projects within user's jurisdiction)
    let passesRoleFilter = true;
    
    // Helper function to normalize strings for comparison
    const normalize = (str?: string): string => {
      if (!str) return '';
      return str.toLowerCase().trim();
    };
    
    if (currentUser) {
      
      if (currentUser.role === UserRole.FACILITY_USER && currentUser.facility) {
        // Facility users can only see their facility's projects
        passesRoleFilter = normalize(projectFacility) === normalize(currentUser.facility);
      } else if (currentUser.role === UserRole.SUBCOUNTY_USER && currentUser.subCounty) {
        // Subcounty users can see all projects in their subcounty
        passesRoleFilter = normalize(projectSubcounty) === normalize(currentUser.subCounty);
      } else if (currentUser.role === UserRole.COUNTY_USER && currentUser.county) {
        // County users can see all projects in their county
        const normalizedProjectCounty = normalize(projectCounty);
        const normalizedUserCounty = normalize(currentUser.county);
        
        passesRoleFilter = normalizedProjectCounty === normalizedUserCounty;
        
      }
    }
    
    // Then apply UI filters (only if project passes the role filter)
    let passesUIFilters = true;
    if (passesRoleFilter) {
      
      // Geographic filtering with hierarchical awareness
      let passesCountyFilter = true;
      let passesSubcountyFilter = true;
      let passesFacilityFilter = true;
      
      // County filter (case insensitive using normalize function)
      if (appliedFilters.county !== 'all') {
        // Compare case-insensitive, handle undefined
        if (!projectCounty) {
          passesCountyFilter = false;
        } else {
          passesCountyFilter = normalize(projectCounty) === normalize(appliedFilters.county);
        }
      }

      // Subcounty filter (case insensitive and respecting hierarchy)
      if (appliedFilters.subcounty !== 'all') {
        // If county is filtered, only check subcounty if project passed county filter
        if (appliedFilters.county === 'all' || passesCountyFilter) {
          passesSubcountyFilter = normalize(projectSubcounty) === normalize(appliedFilters.subcounty);
        }
      }

      // Facility filter (case insensitive and respecting hierarchy)
      if (appliedFilters.facility !== 'all') {
        // If subcounty is filtered, only check facility if project passed subcounty filter
        // If county is filtered but subcounty is not, only check facility if project passed county filter
        const checkFacility = (appliedFilters.subcounty === 'all' && appliedFilters.county === 'all') ||
                              (appliedFilters.subcounty !== 'all' && passesSubcountyFilter) ||
                              (appliedFilters.subcounty === 'all' && appliedFilters.county !== 'all' && passesCountyFilter);

        if (checkFacility) {
          passesFacilityFilter = normalize(projectFacility) === normalize(appliedFilters.facility);
        } else {
          // If parent filters failed, this should also fail
          passesFacilityFilter = false;
        }
      }
      
      // All geographic filters must pass
      passesUIFilters = passesCountyFilter && passesSubcountyFilter && passesFacilityFilter;
      
    }

    const startDate = appliedFilters.fromDate?.toDate() || null;
    const endDate = appliedFilters.toDate?.toDate() || null;
    const projectDate = project.createdAt ? dayjs(project.createdAt).toDate() : new Date();
    

    const matchesDateRange = (!startDate || projectDate >= startDate) &&
      (!endDate || projectDate <= endDate);
    const passes = matchesSearch && matchesCQIStatus && matchesDomain && 
                 matchesSubDomain && matchesDateRange && passesRoleFilter && passesUIFilters;

    return passes;
  });

  return (
    <>
    <Box sx={{ flexGrow: 1 }}>
        <TopBar onLogout={onLogout} />
    </Box>
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => setShowFilters(!showFilters)} color="primary">
              <FilterListIcon />
            </IconButton>
            <Typography variant="h6">Projects</Typography>
          </Box>
        </Box>
        {location.state?.message && (
          <Typography color="success.main" sx={{ mt: 2 }}>
            {location.state.message}
          </Typography>
        )}
      </Box>


      {showFilters && (
      <Paper sx={{ p: 3, mb: 4 }} elevation={1}>
        <Box display="grid" gap={3} sx={{ gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' } }}>
          {/* Filter action buttons */}
          <Box
            gridColumn={{ xs: '1', md: 'span 12' }}
            sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
            >
              Add filter
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearFilters}
            >
              Clear filter
            </Button>
          </Box>
          <Box gridColumn={{ xs: '1', md: 'span 2' }}>
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Search Projects"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by title, problem, or responsible person"
              />
            </Box>
          </Box>
          {/* CQI Status Filter - Only show if there are ongoing projects */}
          {(() => {
            const hasOngoingProjects = projects.some(project => project.status === 'ongoing');
            return hasOngoingProjects ? (
              <Box gridColumn={{ xs: '1', md: 'span 2' }}>
                <Box sx={{ width: '100%' }}>
                  <FormControl fullWidth>
                    <InputLabel>CQI Status</InputLabel>
                    <Select
                      value={filters.cqiStatus}
                      label="CQI Status"
                      onChange={(e) => {
                        const newStatus = e.target.value as FilterCQIStatus;
                        setFilters(prev => ({ 
                          ...prev, 
                          cqiStatus: newStatus,
                          // Reset subset filter when changing main status
                          onCourseSubset: newStatus === 'on_course' ? prev.onCourseSubset : 'all'
                        }));
                      }}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="on_course">🟢 On Course</MenuItem>
                      <MenuItem value="past_due">🟠 Past Due</MenuItem>
                      <MenuItem value="stalled">🔴 Stalled</MenuItem>
                      <MenuItem value="abandoned">⚫ Abandoned</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : null;
          })()}
          
          {/* On Course Subset Filter - Only show when "On Course" is selected */}
          {(() => {
            const hasOngoingProjects = projects.some(project => project.status === 'ongoing');
            const showSubsetFilter = hasOngoingProjects && filters.cqiStatus === 'on_course';
            
            return showSubsetFilter ? (
              <Box gridColumn={{ xs: '1', md: 'span 2' }}>
                <Box sx={{ width: '100%' }}>
                  <FormControl fullWidth>
                    <InputLabel>On Course Subset</InputLabel>
                    <Select
                      value={filters.onCourseSubset}
                      label="On Course Subset"
                      onChange={(e) => setFilters(prev => ({ ...prev, onCourseSubset: e.target.value as FilterOnCourseSubset }))}
                    >
                      <MenuItem value="all">All On Course</MenuItem>
                      <MenuItem value="up_to_date">🟢 Up to Date</MenuItem>
                      <MenuItem value="due_for_update">🟠 Due for Update</MenuItem>
                      <MenuItem value="due_today">🔴 Due Today</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : null;
          })()}
          
          {/* QI Project Domain Filter */}
          <Box gridColumn={{ xs: '1', md: 'span 1' }}>
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel>Domain</InputLabel>
                <Select
                  value={filters.domain}
                  label="Domain"
                  onChange={(e) => {
                    const domain = e.target.value;
                    // Reset subdomain when domain changes
                    setFilters(prev => ({ ...prev, domain, subDomain: 'all' }));
                  }}
                >
                  <MenuItem value="all">All Domains</MenuItem>
                  {CHS_DOMAINS.map(domain => (
                    <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {/* Project Sub Domain Filter */}
          <Box gridColumn={{ xs: '1', md: 'span 1' }}>
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel>Sub Domain</InputLabel>
                <Select
                  value={filters.subDomain}
                  label="Sub Domain"
                  onChange={(e) => setFilters(prev => ({ ...prev, subDomain: e.target.value }))}
                >
                  <MenuItem value="all">All Sub Domains</MenuItem>
                  {filters.domain !== 'all' && CHS_SUBDOMAINS[filters.domain] && 
                    CHS_SUBDOMAINS[filters.domain].map(subDomain => (
                      <MenuItem key={subDomain} value={subDomain}>{subDomain}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* County filter - only visible for NATIONAL_USER and CQI_MANAGER */}
          {(currentUser?.role === UserRole.NATIONAL_USER || currentUser?.role === UserRole.CQI_MANAGER) && (
            <Box gridColumn={{ xs: '1', md: 'span 1' }}>
              <Box sx={{ width: '100%' }}>
                <FormControl fullWidth>
                  <InputLabel>County</InputLabel>
                  <Select
                    value={filters.county}
                    label="County"
                    onChange={(e) => {
                      const county = e.target.value;
                      // Reset subcounty and facility when county changes
                      setFilters(prev => ({ ...prev, county, subcounty: 'all', facility: 'all' }));
                    }}
                  >
                    <MenuItem value="all">All Counties</MenuItem>
                    {counties.map(county => (
                      <MenuItem key={county.id} value={county.name}>{county.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Subcounty filter - visible for NATIONAL_USER, CQI_MANAGER and COUNTY_USER */}
          {(currentUser?.role === UserRole.NATIONAL_USER || 
            currentUser?.role === UserRole.CQI_MANAGER || 
            currentUser?.role === UserRole.COUNTY_USER) && (
            <Box gridColumn={{ xs: '1', md: 'span 1' }}>
              <Box sx={{ width: '100%' }}>
                <FormControl fullWidth>
                  <InputLabel>Subcounty</InputLabel>
                  <Select
                    value={filters.subcounty}
                    label="Subcounty"
                    onChange={(e) => {
                      const subcounty = e.target.value;
                      // Reset facility when subcounty changes
                      setFilters(prev => ({ ...prev, subcounty, facility: 'all' }));
                    }}
                  >
                    <MenuItem value="all">All Subcounties</MenuItem>
                    {subCounties
                      // Filter subcounties based on selected county or user's county
                      .filter(subcounty => {
                        if (currentUser?.role === UserRole.COUNTY_USER && currentUser.county) {
                          return subcounty.countyName === currentUser.county;
                        }
                        // For national users, filter by selected county
                        return filters.county === 'all' || subcounty.countyName === filters.county;
                      })
                      .map(subcounty => (
                        <MenuItem key={subcounty.id} value={subcounty.name}>{subcounty.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Facility filter - visible for everyone except FACILITY_USER */}
          {currentUser?.role !== UserRole.FACILITY_USER && (
            <Box gridColumn={{ xs: '1', md: 'span 1' }}>
              <Box sx={{ width: '100%' }}>
                <FormControl fullWidth>
                  <InputLabel>Facility</InputLabel>
                  <Select
                    value={filters.facility}
                    label="Facility"
                    onChange={(e) => setFilters(prev => ({ ...prev, facility: e.target.value }))}
                  >
                    <MenuItem value="all">All CHUs/WITs</MenuItem>
                    {facilities
                      .filter(facility => {
                        // For SUBCOUNTY_USER, only show facilities in their subcounty
                        if (currentUser?.role === UserRole.SUBCOUNTY_USER && currentUser.subCounty) {
                          return facility.subCountyName === currentUser.subCounty;
                        }
                        
                        // For COUNTY_USER, only show facilities in subcounties of their county
                        if (currentUser?.role === UserRole.COUNTY_USER && currentUser.county) {
                          // Get all subcounties in their county
                          const countySubcounties = subCounties
                            .filter(sc => sc.countyName === currentUser.county)
                            .map(sc => sc.name);
                          return countySubcounties.includes(facility.subCountyName);
                        }
                        
                        // Filter facilities based on selected subcounty
                        if (filters.subcounty !== 'all') {
                          return facility.subCountyName === filters.subcounty;
                        }
                        
                        // Filter by county using the subcounty relationship
                        if (filters.county !== 'all') {
                          const countySubcounties = subCounties
                            .filter(sc => sc.countyName === filters.county)
                            .map(sc => sc.name);
                          return countySubcounties.includes(facility.subCountyName);
                        }
                        
                        return true; // Show all facilities if no filters applied
                      })
                      .map(facility => (
                        <MenuItem key={facility.id} value={facility.name}>{facility.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
          <Box gridColumn={{ xs: '1', md: 'span 3' }}>
            <Box sx={{ width: '100%' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={filters.fromDate}
                  onChange={(date) => handleDateChange('fromDate', date ? dayjs(date) : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
          <Box gridColumn={{ xs: '1', md: 'span 3' }}>
            <Box sx={{ width: '100%' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={filters.toDate}
                  onChange={(date) => handleDateChange('toDate', date ? dayjs(date) : null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
      </Paper>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Typography>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Loading projects...</Typography>
        </Box>
      ) : (
        <Box component="div">
          {filteredProjects.length === 0 ? (
            <Typography sx={{ mt: 4, textAlign: 'center' }}>
              No projects found matching your criteria
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
        {filteredProjects.map((project: Project, index: number) => {
          // Get CQI status for ongoing projects
          const cqiStatus = project.status === 'ongoing' ? getCQIProjectStatus(project) : null;
          
          return (
            <Box key={project._id || project.id || `project-${index}`}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: cqiStatus ? `2px solid ${cqiStatus.color}` : 'none',
                  backgroundColor: cqiStatus ? `${cqiStatus.color}15` : 'inherit',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s'
                  },
                  // Add pulse animation for critical statuses
                  ...((cqiStatus?.status === 'due_today' || cqiStatus?.status === 'stalled') && {
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: `0 0 0 0 ${cqiStatus.color}70`
                      },
                      '70%': {
                        boxShadow: `0 0 0 10px ${cqiStatus.color}00`
                      },
                      '100%': {
                        boxShadow: `0 0 0 0 ${cqiStatus.color}00`
                      }
                    },
                    animation: 'pulse 2s infinite'
                  })
                }}
                onClick={(event) => handleProjectClick(project, event)}
              >
                <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                      {index + 1}. {project.projectTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                       Problem Statement: {project.problemStatement}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    {/* Show CQI status for ongoing projects */}
                    {cqiStatus && (
                      <Chip 
                        label={`${cqiStatus.icon} ${cqiStatus.label}`}
                        size="small"
                        sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: cqiStatus.color,
                          color: 'white',
                          animation: (cqiStatus.status === 'due_today' || cqiStatus.status === 'stalled') ? 'pulse 2s infinite' : 'none',
                          '& .MuiChip-label': {
                            fontSize: '0.75rem'
                          }
                        }}
                      />
                    )}
                    
                    {/* Show regular status for non-ongoing projects */}
                    {project.status !== 'ongoing' && (
                      <Chip 
                        label={
                          project.status === 'archived' 
                            ? 'Shared on Learning Forum' 
                            : project.status === 'completed'
                            ? 'Completed'
                            : project.status === 'draft'
                            ? 'Draft'
                            : project.status?.charAt(0).toUpperCase() + project.status?.slice(1) || 'Unknown'
                        }
                        size="small"
                        color={
                          project.status === 'archived' ? 'primary' : 
                          project.status === 'completed' ? 'success' : 
                          project.status === 'draft' ? 'warning' : 'default'
                        }
                        sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: 
                            project.status === 'archived' ? '#2196F3' : 
                            project.status === 'completed' ? '#4CAF50' : 
                            project.status === 'draft' ? '#FF9800' : undefined,
                          color: 
                            project.status === 'archived' || project.status === 'completed' || project.status === 'draft' ? 'white' : undefined
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Responsible Person: {project.responsiblePerson}
                  </Typography>
                  {/* <Typography variant="body2" color="text.secondary" gutterBottom>
                    Frequency: {project.measurementFrequency}
                  </Typography> */}
                  
                  {/* Show next due date for ongoing projects */}
                  {project.status === 'ongoing' && project.measurementFrequency && (() => {
                    const dueDate = getProjectDueDate(project);
                    
                    if (dueDate) {
                      return (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          gutterBottom
                        >
                          Next Progress Update: {dueDate.toLocaleDateString()}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                  <Typography variant="body2" color="text.secondary">
                    Start Date: {(() => {
                      const raw = project.firstCycleDate || project.startDate;
                      if (!raw) return 'Not set';
                      // Handle DD/MM/YYYY format
                      const parts = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      if (parts) return new Date(+parts[3], +parts[2] - 1, +parts[1]).toLocaleDateString();
                      const d = new Date(raw);
                      return isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString();
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    End Date: {(() => {
                      const raw = project.projectEndDate || project.endDate;
                      if (!raw) return 'Not set';
                      // Handle DD/MM/YYYY format
                      const parts = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                      if (parts) return new Date(+parts[3], +parts[2] - 1, +parts[1]).toLocaleDateString();
                      const d = new Date(raw);
                      return isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString();
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', mt: 2 }}>
                  <Divider orientation="vertical" flexItem />
                  {/* Show number of root causes if available */}
                  {(project.rootCauseItems && project.rootCauseItems.length > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="body2">
                        Root Causes: {project.rootCauseItems.length}
                      </Typography>
                    </Box>
                  )}
                  {/* Show number of action items if available */}
                  {(project.actionItems && project.actionItems.length > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="body2">
                        Actions: {project.actionItems.length}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  
                  {/* Edit Draft button for draft projects */}
                  {project.status === 'draft' && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/add-project?draft=${project.id || project._id}`)}
                      sx={{ 
                        fontWeight: 'bold',
                        backgroundColor: '#FF9800',
                        '&:hover': {
                          backgroundColor: '#F57C00'
                        }
                      }}
                    >
                      Continue Editing Draft
                    </Button>
                  )}
                  
                  {/* Update Progress button for ongoing projects that need updates */}
                  {project.status === 'ongoing' && cqiStatus && (() => {
                    // Show Update Progress button only for users with update permission
                    const needsUpdate = ['due_for_update', 'due_today', 'past_due'].includes(cqiStatus.status);
                    
                    return (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canUpdateProgress() && needsUpdate && (
                          <Button
                            variant="contained"
                            color={cqiStatus.status === 'due_today' || cqiStatus.status === 'past_due' ? 'error' : 'warning'}
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={(e) => handleAddTestCycleClick(e, project)}
                            sx={{ 
                              animation: (cqiStatus.status === 'due_today' || cqiStatus.status === 'past_due') ? 'pulse 2s infinite' : 'none',
                              fontWeight: 'bold'
                            }}
                          >
                            Update Progress
                          </Button>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Box>
          );
        })}
            </Box>
          )}
        </Box>
      )}
      {selectedProject && (
        <UpdateProgress
          open={isAddTestCycleOpen}
          projectId={selectedProject._id || selectedProject.id || ''}
          onClose={() => {
            setIsAddTestCycleOpen(false);
            setSelectedProject(null);
          }}
          onSubmit={handleAddTestCycle}
          onProjectUpdate={(updatedProject) => {

            
            // Update the project in the local projects array
            setProjects(prevProjects => {
              const updatedProjects = prevProjects.map(project => {
                const isMatch = (project._id === updatedProject._id || project.id === updatedProject.id);
                if (isMatch) {
                  return { ...project, ...updatedProject };
                }
                return project;
              });

              return updatedProjects;
            });
            
            // Close the dialog
            setIsAddTestCycleOpen(false);
            setSelectedProject(null);
            
            // Show success notification
            const statusLabel = updatedProject.status === 'archived' 
              ? 'Shared on Learning Platform' 
              : updatedProject.status === 'completed'
              ? 'Completed'
              : updatedProject.status === 'draft'
              ? 'Draft'
              : updatedProject.status;
            setNotification({
              open: true,
              message: `Project status updated to ${statusLabel}`,
              severity: 'success'
            });
            
          }}
        />
      )}
      
      {/* Success/Error Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
    </Container>
    </>
  );
};

export default ProjectList;
